// Partnerships Hub — shared type contracts between frontend and backend

export type PartnerSlug = 'tiktok' | 'google' | 'meta';

export type ProductCategory =
  | 'Motors'
  | 'Experience'
  | 'Amplification'
  | 'Enhancers'
  | 'Phygital';

export type ContentStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type AssetType =
  | 'one-pager'
  | 'deck'
  | 'video'
  | 'image'
  | 'creative-example'
  | 'other';

export type AssetEntityType =
  | 'product'
  | 'package'
  | 'program'
  | 'case'
  | 'framework';

export type ArchivableType =
  | 'product'
  | 'package'
  | 'case'
  | 'faq'
  | 'program'
  | 'framework'
  | 'asset';

// ─── Core entities ────────────────────────────────────────────────────────────

export interface Partner {
  partnerId: string;
  slug: PartnerSlug;
  name: string;
  description: string;
  logoUrl?: string;
  website?: string;
  color: string;
  driveUrl?: string;
  driveFolder?: string;
  internalOwner?: string;
  notes?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Product {
  productId: string;
  partnerId: string;
  name: string;
  tagline: string;
  description: string;
  category: ProductCategory;
  howItWorks: string[];
  keyBenefits: string[];
  whenToUse: string;
  whyItMatters: string;
  availableMarkets: string[];
  pricing?: string;
  deliverables?: string[];
  mediaRequirements?: string;
  creativeRequirements?: string;
  bestPractices?: string;
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  imageUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Program {
  programId: string;
  partnerId: string;
  name: string;
  description: string;
  objective: string;
  availableMarkets: string[];
  eligibilityRequirements?: string;
  includedPackageIds?: string[];
  includedProductIds?: string[];
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  visualUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * Variante (tier) de un pack: "Package A", "Bronze", "M"… (pedido de Partnerships
 * 13/10/2026: una tarjeta por pack con sus variantes adentro, no una por tier).
 * Lo que cambia entre variantes vive acá; lo común (contexto, reglas, elegibilidad,
 * economics…) vive en el pack.
 */
export interface PackageVariant {
  variantId: string;
  name: string;
  /** Código del tier (BRONZE, SPOTLIGHT…) para el orden y el color; opcional. */
  tier?: string;
  deliverables?: string[];
  pricing?: string;
  currency?: string;
  videos?: number;
  minCreators?: number;
  productionFee?: string;
  mediaCommitment?: string;
  notes?: string;
  /** packageId de la fila original antes de unificar: los links viejos siguen abriendo el pack. */
  legacyPackageId?: string;
}

/** Quién vende el pack. El precio lo fija SIEMPRE el partner, venda quien venda. */
export const PACKAGE_SALES_MODELS = ['winclap_direct', 'partner', 'both'] as const;
export type PackageSalesModel = (typeof PACKAGE_SALES_MODELS)[number];

/** A qué cliente aplica: nuevo, recurrente o los dos. */
export const PACKAGE_CLIENT_TYPES = ['new', 'repeated', 'both'] as const;
export type PackageClientType = (typeof PACKAGE_CLIENT_TYPES)[number];

/** Elegibilidad: lo que se usa como filtro en la lista (datos, no texto libre). */
export interface PackageEligibility {
  segments?: string[];
  accountSizes?: string[];
  clientType?: PackageClientType;
  notes?: string;
}

export interface PackageConditions {
  minRoi?: string;
  minMediaSpend?: string;
  investmentWindow?: string;
  flexible?: string;
}

/** INTERNO: nunca va al cliente ni al partner; el asistente tampoco lo lee. */
export interface PackageEconomics {
  incentive?: string;
  costPerAsset?: string;
  margin?: string;
  notes?: string;
}

export interface PackageExternalView {
  shareable?: string;
  materialUrl?: string;
}

export interface PackageLink {
  label: string;
  url: string;
}

export interface Package {
  packageId: string;
  partnerId: string;
  programId?: string;
  name: string;
  description: string;
  targetAudience: string;
  availableMarkets: string[];
  highlights: string[];
  includedProducts: string[];
  deliverables?: string[];
  pricing?: string;
  currency?: string;
  effectiveDate?: string;
  productionFee?: string;
  mediaCommitment?: string;
  driveUrl?: string;
  notes?: string;
  tier?: string;
  saleType?: string;
  saleSubType?: string;
  videos?: number;
  minCreators?: number;
  /** Orden manual dentro del listado (menor = primero). Sin cargar, ordena por tier. */
  sortOrder?: number;
  /** Estado que tenía al archivarlo: "Restaurar" vuelve a ese (un borrador no se publica solo). */
  statusBeforeArchive?: ContentStatus;
  /** Variantes / tiers del pack (13/10/2026). Vacío = pack sin variantes: valen los campos del pack. */
  variants?: PackageVariant[];
  /** Contexto general: objetivo estratégico del pack y lo que dice el partner. */
  context?: string;
  /** SLAs / workflow overview. */
  workflow?: string;
  salesModel?: PackageSalesModel;
  eligibility?: PackageEligibility;
  conditions?: PackageConditions;
  /** INTERNO (ver PackageEconomics). */
  economics?: PackageEconomics;
  externalView?: PackageExternalView;
  /** Reglas con el partner: heads-ups, tiempos de espera, cuándo va vía agencia de medios. */
  partnerRules?: string;
  /** Restricciones de producto. */
  restrictions?: string;
  /** Fin de vigencia (`effectiveDate` es el inicio). */
  validUntil?: string;
  /** Links útiles (deck, one-pager…), además de `driveUrl`. */
  links?: PackageLink[];
  /** Fila que quedó unificada en otro pack (tombstone de la fusión): no se lista; `getPackage` sigue a `mergedInto`. */
  mergedInto?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  /** Última persona que editó (la fecha es `updatedAt`). */
  updatedBy?: string;
}

export interface Case {
  caseId: string;
  partnerId: string;
  title: string;
  brand: string;
  industry: string;
  market: string;
  summary?: string;
  objective: string;
  challenge?: string;
  solution: string;
  results: string[];
  highlights: string[];
  productsUsed?: string[];
  packagesUsed?: string[];
  mediaSpend?: string;
  kpis?: string[];
  driveLinks?: string[];
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface FAQ {
  faqId: string;
  partnerId: string;
  category: string;
  question: string;
  answer: string;
  order: number;
  relatedProductId?: string;
  relatedProgramId?: string;
  relatedPackageId?: string;
  relatedPartnerId?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Framework {
  frameworkId: string;
  partnerId: string;
  name: string;
  description: string;
  methodology?: string;
  useCases?: string[];
  examples?: string[];
  driveUrl?: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Asset {
  assetId: string;
  partnerId: string;
  name: string;
  assetType: AssetType;
  driveUrl: string;
  description?: string;
  tags?: string[];
  fileFormat?: string;
  thumbnailUrl?: string;
  entityId?: string;
  entityType?: AssetEntityType;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Market {
  marketId: string;
  name: string;
  countryCode: string;
  region?: string;
  currency?: string;
  notes?: string;
  active: boolean;
}

export interface PartnershipsAccess {
  canView: boolean;
  canEdit: boolean;
}

// ─── Assistant ────────────────────────────────────────────────────────────────

export interface AssistantReply {
  reply: string;
  suggestedPath?: string;
  driveLinks?: string[];
}

// ─── Mutation inputs ──────────────────────────────────────────────────────────

export interface CreateProductInput {
  partnerId: string;
  name: string;
  tagline: string;
  description: string;
  category: ProductCategory;
  howItWorks: string[];
  keyBenefits: string[];
  whenToUse: string;
  whyItMatters: string;
  availableMarkets: string[];
  pricing?: string;
  deliverables?: string[];
  mediaRequirements?: string;
  creativeRequirements?: string;
  bestPractices?: string;
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  imageUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status?: ContentStatus;
}

/**
 * `expectedUpdatedAt` — el `updatedAt` que el cliente leyó cuando abrió el
 * formulario. El backend solo escribe si el item sigue igual; si otra persona lo
 * editó mientras tanto, devuelve CONFLICT en vez de pisarle el cambio. Opcional:
 * sin él la escritura va sin condición, como antes.
 */
export interface UpdateProductInput {
  productId: string;
  name?: string;
  tagline?: string;
  description?: string;
  category?: ProductCategory;
  howItWorks?: string[];
  keyBenefits?: string[];
  whenToUse?: string;
  whyItMatters?: string;
  availableMarkets?: string[];
  pricing?: string;
  deliverables?: string[];
  mediaRequirements?: string;
  creativeRequirements?: string;
  bestPractices?: string;
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  imageUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateProgramInput {
  partnerId: string;
  name: string;
  description: string;
  objective: string;
  availableMarkets: string[];
  eligibilityRequirements?: string;
  includedPackageIds?: string[];
  includedProductIds?: string[];
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  visualUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status?: ContentStatus;
}

export interface UpdateProgramInput {
  programId: string;
  name?: string;
  description?: string;
  objective?: string;
  availableMarkets?: string[];
  eligibilityRequirements?: string;
  includedPackageIds?: string[];
  includedProductIds?: string[];
  relatedFaqIds?: string[];
  relatedAssetIds?: string[];
  visualUrls?: string[];
  driveUrl?: string;
  notes?: string;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreatePackageInput {
  partnerId: string;
  programId?: string;
  name: string;
  description: string;
  targetAudience: string;
  availableMarkets: string[];
  highlights: string[];
  includedProducts: string[];
  deliverables?: string[];
  pricing?: string;
  currency?: string;
  effectiveDate?: string;
  productionFee?: string;
  mediaCommitment?: string;
  driveUrl?: string;
  notes?: string;
  tier?: string;
  saleType?: string;
  saleSubType?: string;
  videos?: number;
  minCreators?: number;
  sortOrder?: number;
  variants?: PackageVariant[];
  context?: string;
  workflow?: string;
  salesModel?: PackageSalesModel;
  eligibility?: PackageEligibility;
  conditions?: PackageConditions;
  economics?: PackageEconomics;
  externalView?: PackageExternalView;
  partnerRules?: string;
  restrictions?: string;
  validUntil?: string;
  links?: PackageLink[];
  status?: ContentStatus;
}

/**
 * En una edición, `null` (o `''`) = BORRAR el campo: `undefined` se pierde en el JSON y
 * el valor viejo quedaba (bug del 07/10 con tier/orden, y otra vez el 13/10 con la ficha).
 */
export interface UpdatePackageInput {
  packageId: string;
  programId?: string | null;
  name?: string;
  description?: string;
  targetAudience?: string;
  availableMarkets?: string[];
  highlights?: string[];
  includedProducts?: string[];
  deliverables?: string[];
  pricing?: string | null;
  currency?: string | null;
  effectiveDate?: string | null;
  productionFee?: string | null;
  mediaCommitment?: string | null;
  driveUrl?: string | null;
  notes?: string | null;
  tier?: string | null;
  saleType?: string | null;
  saleSubType?: string | null;
  videos?: number | null;
  minCreators?: number | null;
  sortOrder?: number | null;
  variants?: PackageVariant[];
  context?: string | null;
  workflow?: string | null;
  salesModel?: PackageSalesModel | null;
  eligibility?: PackageEligibility;
  conditions?: PackageConditions;
  economics?: PackageEconomics;
  externalView?: PackageExternalView;
  partnerRules?: string | null;
  restrictions?: string | null;
  validUntil?: string | null;
  links?: PackageLink[];
  statusBeforeArchive?: ContentStatus;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateCaseInput {
  partnerId: string;
  title: string;
  brand: string;
  industry: string;
  market: string;
  summary?: string;
  objective: string;
  challenge?: string;
  solution: string;
  results: string[];
  highlights: string[];
  productsUsed?: string[];
  packagesUsed?: string[];
  mediaSpend?: string;
  kpis?: string[];
  driveLinks?: string[];
  status?: ContentStatus;
}

export interface UpdateCaseInput {
  caseId: string;
  title?: string;
  brand?: string;
  industry?: string;
  market?: string;
  summary?: string;
  objective?: string;
  challenge?: string;
  solution?: string;
  results?: string[];
  highlights?: string[];
  productsUsed?: string[];
  packagesUsed?: string[];
  mediaSpend?: string;
  kpis?: string[];
  driveLinks?: string[];
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateFAQInput {
  partnerId: string;
  category: string;
  question: string;
  answer: string;
  order?: number;
  relatedProductId?: string;
  relatedProgramId?: string;
  relatedPackageId?: string;
  relatedPartnerId?: string;
  status?: ContentStatus;
}

export interface UpdateFAQInput {
  faqId: string;
  category?: string;
  question?: string;
  answer?: string;
  order?: number;
  relatedProductId?: string;
  relatedProgramId?: string;
  relatedPackageId?: string;
  relatedPartnerId?: string;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateFrameworkInput {
  partnerId: string;
  name: string;
  description: string;
  methodology?: string;
  useCases?: string[];
  examples?: string[];
  driveUrl?: string;
  status?: ContentStatus;
}

export interface UpdateFrameworkInput {
  frameworkId: string;
  name?: string;
  description?: string;
  methodology?: string;
  useCases?: string[];
  examples?: string[];
  driveUrl?: string;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateAssetInput {
  partnerId: string;
  name: string;
  assetType: AssetType;
  driveUrl: string;
  description?: string;
  tags?: string[];
  fileFormat?: string;
  thumbnailUrl?: string;
  entityId?: string;
  entityType?: AssetEntityType;
  status?: ContentStatus;
}

export interface UpdateAssetInput {
  assetId: string;
  name?: string;
  assetType?: AssetType;
  driveUrl?: string;
  description?: string;
  tags?: string[];
  fileFormat?: string;
  thumbnailUrl?: string;
  entityId?: string;
  entityType?: AssetEntityType;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreatePartnerInput {
  slug: PartnerSlug;
  name: string;
  description: string;
  color: string;
  logoUrl?: string;
  website?: string;
  driveUrl?: string;
  driveFolder?: string;
  internalOwner?: string;
  notes?: string;
  status?: ContentStatus;
}

export interface UpdatePartnerInput {
  partnerId: string;
  name?: string;
  description?: string;
  color?: string;
  logoUrl?: string;
  website?: string;
  driveUrl?: string;
  driveFolder?: string;
  internalOwner?: string;
  notes?: string;
  status?: ContentStatus;
  expectedUpdatedAt?: string;
}

export interface CreateMarketInput {
  name: string;
  countryCode: string;
  region?: string;
  currency?: string;
  notes?: string;
}

export interface UpdateMarketInput {
  marketId: string;
  name?: string;
  countryCode?: string;
  region?: string;
  currency?: string;
  notes?: string;
  active?: boolean;
}
